import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, Check, Phone, MapPin, Mail } from "lucide-react";
import { PRODUCTS } from "@/data/products";

const LOGO = "/assets/cravy-logo.png";

export default function Products() {
  useEffect(() => {
    document.title = "Cravy — Our Lineup of Smart Markets";
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="font-body bg-cravy-cream min-h-screen">
      <ProductHeader />

      {/* Hero */}
      <section className="bg-cravy-ink cravy-grain pt-[72px] relative overflow-hidden">
        <div className="absolute -top-32 -right-20 w-[520px] h-[520px] rounded-full bg-cravy-orange/15 blur-[120px]" />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-10 pt-20 lg:pt-28 pb-16 lg:pb-24">
          <Link to="/" className="text-cravy-cream/60 hover:text-cravy-orange text-[13px] inline-flex items-center gap-2" data-testid="products-back-home">
            ← Back to home
          </Link>
          <div className="mt-6 inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-cravy-orange/10 border border-cravy-orange/25 text-cravy-orange text-[12px] font-semibold tracking-wide uppercase">
            <Sparkles className="w-3.5 h-3.5" /> Our lineup
          </div>
          <h1 className="mt-6 font-display font-extrabold text-cravy-cream tracking-[-0.03em] leading-[0.95] text-[44px] sm:text-[64px] lg:text-[80px] max-w-4xl">
            One catalog. <span className="text-cravy-orange italic">Every space.</span>
          </h1>
          <p className="mt-6 text-cravy-cream/70 text-[17px] lg:text-[19px] max-w-2xl leading-relaxed">
            From a single-door cooler tucked into a waiting room to a full open-concept micro-market for 300-unit communities — Cravy matches the right format to your foot traffic, footprint, and brand.
          </p>
        </div>
      </section>

      {/* Catalog */}
      <section className="bg-cravy-cream py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-5 lg:px-10">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {PRODUCTS.map((p, i) => (
              <Link
                key={p.slug}
                to={`/products/${p.slug}`}
                className="group bg-white rounded-3xl p-5 border border-cravy-ink/5 hover:shadow-2xl hover:-translate-y-1.5 transition-all flex flex-col"
                data-testid={`product-card-${p.slug}`}
              >
                <div className="aspect-square rounded-2xl bg-white flex items-center justify-center overflow-hidden relative">
                  <img src={p.image} alt={p.name} className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500" />
                  {p.badge && (
                    <div className="absolute top-3 left-3 inline-flex items-center px-2.5 py-1 rounded-full bg-cravy-orange text-cravy-ink text-[10px] font-bold uppercase tracking-wider shadow-md">
                      {p.badge}
                    </div>
                  )}
                  <div className="absolute top-3 right-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-cravy-orange/15 text-cravy-orangeDeep text-[10px] font-bold uppercase tracking-wider">
                    <span className="w-1.5 h-1.5 rounded-full bg-cravy-orange animate-pulse" /> In rotation
                  </div>
                </div>
                <div className="mt-5 flex-1 flex flex-col">
                  <div className="text-cravy-orangeDeep text-[11px] font-bold uppercase tracking-[0.15em]">{p.category}</div>
                  <div className="mt-1.5 font-display font-bold text-[22px] tracking-tight">{p.name}</div>
                  <p className="mt-2 text-cravy-ink/65 text-[14px] leading-relaxed flex-1">{p.short}</p>
                  <div className="mt-5 flex items-center gap-1.5 text-cravy-orangeDeep text-[13px] font-semibold group-hover:gap-2.5 transition-all">
                    Learn more <ArrowRight className="w-4 h-4" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA strip */}
      <section className="bg-cravy-ink cravy-grain py-20 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full bg-cravy-orange/12 blur-[140px]" />
        <div className="relative max-w-4xl mx-auto px-5 lg:px-10 text-center">
          <h2 className="font-display font-extrabold text-cravy-cream text-[36px] sm:text-[48px] lg:text-[56px] leading-[0.95] tracking-[-0.03em]">
            Not sure which one fits?
          </h2>
          <p className="mt-5 text-cravy-cream/70 text-[16px] max-w-xl mx-auto">
            Tell us about your space — we'll recommend the right format in a 10-minute walkthrough.
          </p>
          <Link
            to="/#partner"
            className="btn-orange mt-8 inline-flex items-center gap-2 px-7 py-4 rounded-full text-[15px]"
            data-testid="products-cta-partner"
          >
            Partner with Cravy <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      <ProductFooter />
    </div>
  );
}

export function ProductHeader() {
  return (
    <header className="fixed top-0 inset-x-0 z-50 backdrop-blur-md bg-cravy-ink/80 border-b border-white/5">
      <nav className="max-w-7xl mx-auto px-5 lg:px-10 h-[72px] flex items-center justify-between">
        <Link to="/" className="flex items-center" data-testid="products-nav-logo">
          <img src={LOGO} alt="Cravy" className="h-12 w-12 object-contain" />
        </Link>
        <div className="hidden md:flex items-center gap-7 text-cravy-cream/80 text-[14px] font-medium">
          <Link to="/products" className="hover:text-cravy-cream transition">Products</Link>
          <Link to="/residents" className="hover:text-cravy-cream transition">Residents</Link>
          <Link to="/about" className="hover:text-cravy-cream transition">About</Link>
          <Link to="/refer" className="hover:text-cravy-cream transition">Refer & Earn</Link>
        </div>
        <Link to="/#partner" className="btn-orange hidden sm:inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-[14px]" data-testid="products-nav-cta">
          Partner with us <ArrowRight className="w-4 h-4" />
        </Link>
      </nav>
    </header>
  );
}

export function ProductFooter() {
  return (
    <footer className="bg-cravy-ink border-t border-white/5">
      <div className="max-w-7xl mx-auto px-5 lg:px-10 py-14 grid md:grid-cols-12 gap-10">
        <div className="md:col-span-5">
          <div className="flex items-center">
            <img src={LOGO} alt="Cravy" className="h-14 w-14 object-contain" />
          </div>
          <p className="mt-6 text-cravy-cream/60 text-[14px] max-w-sm leading-relaxed">
            Premium smart markets installed, stocked, and managed at no cost to your property. Operated by TK Love LLC.
          </p>
        </div>
        <div className="md:col-span-3">
          <div className="text-cravy-cream/40 text-[11px] uppercase tracking-[0.2em] mb-4">Explore</div>
          <ul className="space-y-3 text-cravy-cream/75 text-[14px]">
            <li><Link to="/" className="hover:text-cravy-orange transition">Home</Link></li>
            <li><Link to="/products" className="hover:text-cravy-orange transition">Products</Link></li>
            <li><Link to="/residents" className="hover:text-cravy-orange transition">For Residents</Link></li>
            <li><Link to="/about" className="hover:text-cravy-orange transition">About</Link></li>
            <li><Link to="/refer" className="hover:text-cravy-orange transition">Refer &amp; Earn</Link></li>
          </ul>
        </div>
        <div className="md:col-span-4">
          <div className="text-cravy-cream/40 text-[11px] uppercase tracking-[0.2em] mb-4">Contact</div>
          <ul className="space-y-3 text-cravy-cream/75 text-[14px]">
            <li className="flex items-center gap-2"><Phone className="w-4 h-4 text-cravy-orange" /> Jayson · <a className="hover:text-cravy-cream" href="tel:13149138897">(314) 913-8897</a></li>
            <li className="flex items-center gap-2"><Mail className="w-4 h-4 text-cravy-orange" /> <a className="hover:text-cravy-cream" href="mailto:Jayson@cravyco.com">Jayson@cravyco.com</a></li>
            <li className="flex items-center gap-2"><Phone className="w-4 h-4 text-cravy-orange" /> Ted · <a className="hover:text-cravy-cream" href="tel:13147058356">(314) 705-8356</a></li>
            <li className="flex items-center gap-2"><Mail className="w-4 h-4 text-cravy-orange" /> <a className="hover:text-cravy-cream" href="mailto:Ted@cravyco.com">Ted@cravyco.com</a></li>
          </ul>
        </div>
      </div>
      <div className="border-t border-white/5">
        <div className="max-w-7xl mx-auto px-5 lg:px-10 py-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-cravy-cream/40 text-[12px]">
          <div>© {new Date().getFullYear()} TK Love LLC · Cravy is a trademark of TK Love LLC.</div>
          <Link to="/admin/login" className="hover:text-cravy-cream/70 transition" data-testid="products-footer-admin">Admin</Link>
        </div>
      </div>
    </footer>
  );
}
